import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  useParams, useNavigate, useLocation
} from 'react-router-dom';
import api from '../api/api';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import {
  formatDistanceToNow
} from 'date-fns';
import toast from 'react-hot-toast';
import ErrorBoundary from '../components/ErrorBoundary';
import MenuItemImage from '../components/MenuItemImage';
import {
  useCart
} from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { ORDER_EVENTS } from '../constants/socketEvents';
import { formatCurrency } from '../config/currency';
import { DisplayContext, formatOrderId } from '../utils/orderFormatter';
import { useTheme } from '../context/ThemeContext';
import { CURRENCY } from '../config/currency';

// Item styling for modern theme
const itemKeywordStyles: { [key: string]: { emoji: string; color: string } } = {
  coffee: { emoji: '☕', color: 'var(--status-warn-text)' },
  cappuccino: { emoji: '☕', color: '#6F4E37' }, /* basil-todo */
  latte: { emoji: '☕', color: '#C4A484' }, /* basil-todo */
  tea: { emoji: '🍵', color: '#228B22' }, /* basil-todo */
  juice: { emoji: '🧃', color: 'var(--status-warn)' },
  orange: { emoji: '🍊', color: 'var(--status-warn)' },
  smoothie: { emoji: '🥤', color: '#FF69B4' }, /* basil-todo */
  pizza: { emoji: '🍕', color: '#FF6347' }, /* basil-todo */
  burger: { emoji: '🍔', color: '#CD853F' }, /* basil-todo */
  sandwich: { emoji: '🥪', color: '#DAA520' }, /* basil-todo */
  pasta: { emoji: '🍝', color: 'var(--status-warn)' },
  noodles: { emoji: '🍜', color: '#FFD700' }, /* basil-todo */
  biryani: { emoji: '🍛', color: '#FF8C00' }, /* basil-todo */
  curry: { emoji: '🍛', color: '#B8860B' }, /* basil-todo */
  chicken: { emoji: '🍗', color: '#D2691E' }, /* basil-todo */
  fish: { emoji: '🐟', color: '#4682B4' }, /* basil-todo */
  soup: { emoji: '🍲', color: '#FF7F50' }, /* basil-todo */
  salad: { emoji: '🥗', color: '#32CD32' }, /* basil-todo */
  caesar: { emoji: '🥗', color: '#32CD32' }, /* basil-todo */
  fries: { emoji: '🍟', color: '#FFD700' }, /* basil-todo */
  fry: { emoji: '🍗', color: '#D2691E' }, /* basil-todo */
  cake: { emoji: '🍰', color: '#FF69B4' }, /* basil-todo */
  icecream: { emoji: '🍨', color: '#FFB6C1' }, /* basil-todo */
  bruschetta: { emoji: '🥖', color: '#CD853F' }, /* basil-todo */
  bread: { emoji: '🍞', color: '#D2691E' }, /* basil-todo */
  default: { emoji: '🍽️', color: 'var(--accent-alt)' }
};

const getItemEmoji = (itemName: string): { emoji: string; color: string } => {
  const lowerName = itemName.toLowerCase();
  for (const [keyword, style] of Object.entries(itemKeywordStyles)) {
    if (keyword !== 'default' && lowerName.includes(keyword)) {
      return style;
    }
  }
  return itemKeywordStyles.default;
};

interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
}

interface OrderItem {
  id: number;
  quantity: number;
  price: number;
  menuItem: MenuItem;
  imageUrl?: string;
  comment?: string;
  toppings?: { id: number; name: string }[];
}

interface Vendor {
  id: number;
  username: string;
}

interface QRCode {
  code: string;
}

interface Order {
  id: number;
  customOrderId?: string;
  status: string;
  createdAt: string;
  items: OrderItem[];
  vendor: Vendor;
  qrCode?: QRCode;
  total: number;
  deliveryType: string;
  // Table info
  tableName?: string;
  floorName?: string;
  sectionName?: string;
  // Tax breakdown fields
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  taxType?: string;
}

// Wrap the entire component with ErrorBoundary to catch render errors
const OrderDetailWithErrorBoundary: React.FC = () => {
  return (
    <ErrorBoundary fallback={
      <div className="p-6 bg-red-50 dark:bg-red-900 rounded-lg text-center">
        <h3 className="text-xl font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Order</h3>
        <p className="text-red-700 dark:text-red-300 mb-4">There was a problem displaying this order.</p>
        <button 
          onClick={() => window.location.href = '/qr'} 
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Return to Menu
        </button>
      </div>
    }>
      <OrderDetail />
    </ErrorBoundary>
  );
};

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hydrateCartFromOrder } = useCart();
  const { isAuthenticated, isGuest } = useAuth();
  const socket = useSocket();
  const subscribedRef = useRef<boolean>(false);
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromMyCart, setIsFromMyCart] = useState<boolean>(false);
  
  // Check if coming from My Cart
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setIsFromMyCart(searchParams.get('source') === 'mycart');
  }, [location.search]);
  
  const fetchOrderDetails = useCallback(async () => {
    console.log('🔍 OrderDetail - Fetching order:', { orderId, urlPath: window.location.pathname });
    // Check if orderId is valid (not undefined, null, or the string "undefined")
    if (!orderId || orderId === 'undefined') {
      // Handle invalid order ID (including the string "undefined")
      setError(orderId === 'undefined' ? "Invalid order reference. The order ID is missing." : "Order ID is missing.");
      setLoading(false);

      // Navigate back to a safe place
      toast.error("Invalid order reference. Redirecting to orders page.");
      setTimeout(() => {
        navigate('/orders');
      }, 1500);
      return;
    }

    setLoading(true);

    try {
      // Strategy: Try public endpoint first (works for anonymous orders)
      // If it fails with 403 (requires auth), try authenticated endpoint or redirect to login
      console.log('🔍 OrderDetail - Trying public endpoint first:', `/orders/public/${orderId}`);
      const res = await api.get<Order>(`/orders/public/${orderId}`);

      console.log('🔍 OrderDetail - Received order via public endpoint:', res.data);
      if (!res.data || !res.data.id) {
        console.error("Received invalid order data:", res.data);
        setError("Invalid order data received from server.");
        return;
      }
      setOrder(res.data);
      setError(null);
    } catch (err: any) {
      // Handle 403 - order requires authentication
      if (err.response?.status === 403 && err.response?.data?.requiresAuth) {
        console.log('🔍 OrderDetail - Order requires authentication');

        // If user is authenticated, try the authenticated endpoint
        if (isAuthenticated || isGuest) {
          try {
            console.log('🔍 OrderDetail - User authenticated, trying:', `/orders/${orderId}`);
            const authRes = await api.get<Order>(`/orders/${orderId}`);

            console.log('🔍 OrderDetail - Received order via authenticated endpoint:', authRes.data);
            if (!authRes.data || !authRes.data.id) {
              console.error("Received invalid order data:", authRes.data);
              setError("Invalid order data received from server.");
              return;
            }
            setOrder(authRes.data);
            setError(null);
            return;
          } catch (authErr: any) {
            console.error("Failed to fetch order with authentication:", authErr);
            setError(authErr.response?.data?.message || "Failed to load order details.");

            if (isFromMyCart) {
              toast.error("Couldn't load your current order. Redirecting to menu.");
              setTimeout(() => navigate('/qr'), 1500);
            }
            return;
          }
        }

        // Not authenticated - redirect to login
        console.log('🔍 OrderDetail - User not authenticated, redirecting to login');
        toast.error('Please log in to view this order');
        navigate('/login', { state: { from: `/orders/${orderId}` } });
        return;
      }

      // Handle other errors
      console.error("Failed to fetch order:", err);
      setError(err.response?.data?.message || "Failed to load order details.");

      if (isFromMyCart) {
        toast.error("Couldn't load your current order. Redirecting to menu.");
        setTimeout(() => navigate('/qr'), 1500);
      }
    } finally {
      setLoading(false);
    }
  }, [orderId, isFromMyCart, navigate, isAuthenticated, isGuest]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  // Subscribe to real-time updates for this order
  useEffect(() => {
    if (socket && orderId && !subscribedRef.current) {
      const orderIdNum = parseInt(orderId, 10);
      if (!isNaN(orderIdNum)) {
        console.log(`[OrderDetail] Subscribing to order ${orderIdNum}`);
        socket.subscribeToOrder(orderIdNum);
        subscribedRef.current = true;
      }
    }

    return () => {
      // Cleanup on unmount
      if (socket && orderId && subscribedRef.current) {
        const orderIdNum = parseInt(orderId, 10);
        if (!isNaN(orderIdNum)) {
          console.log(`[OrderDetail] Unsubscribing from order ${orderIdNum}`);
          socket.unsubscribeFromOrder(orderIdNum);
          subscribedRef.current = false;
        }
      }
    };
  }, [socket, orderId]);

  // Listen for order status updates
  useEffect(() => {
    if (!socket || !orderId) return;

    const handleOrderUpdate = (data: any) => {
      const eventOrderId = typeof data.orderId === 'string' ? parseInt(data.orderId, 10) : data.orderId;
      const currentOrderId = parseInt(orderId, 10);

      if (eventOrderId === currentOrderId) {
        console.log('[OrderDetail] Real-time update received:', data);

        // Update order status
        setOrder(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            status: data.status || prev.status
          };
        });

        // Show toast notification for status change
        if (data.status) {
          const statusMessages: Record<string, string> = {
            'ACCEPTED': 'Your order has been accepted!',
            'PREPARING': 'Your order is being prepared!',
            'READY': 'Your order is ready!',
            'COMPLETED': 'Your order has been completed!',
            'CANCELLED': 'Your order has been cancelled.'
          };
          const message = statusMessages[data.status];
          if (message) {
            if (data.status === 'CANCELLED') {
              toast.error(message);
            } else {
              toast.success(message);
            }
          }
        }
      }
    };

    socket.on(ORDER_EVENTS.STATUS_UPDATE, handleOrderUpdate);
    socket.on(ORDER_EVENTS.UPDATED, handleOrderUpdate);

    return () => {
      socket.off(ORDER_EVENTS.STATUS_UPDATE, handleOrderUpdate);
      socket.off(ORDER_EVENTS.UPDATED, handleOrderUpdate);
    };
  }, [socket, orderId]);

  // Handle back navigation
  const handleBack = () => {
    if (isFromMyCart) {
      navigate('/qr');
    } else {
      navigate('/orders');
    }
  };
  
  // Handle cancel order
  const handleCancelOrder = async () => {
    if (!order) return;
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success('Order has been cancelled');
      // Always refresh order details to show updated status
      fetchOrderDetails();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to cancel order';
      toast.error(msg);
    }
  };

  const getStatusColor = (status: string | undefined): string => {
    // Defensive check for undefined status
    if (!status) {
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100';
    }

    // Map the status to CSS classes based on the status
    switch (status.toUpperCase()) {
      case 'PENDING': return 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100';
      case 'ACCEPTED': return 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100';
      case 'PREPARING': return 'bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-100';
      case 'READY':
      case 'READY_FOR_PICKUP': return 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100';
      case 'COMPLETED': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100';
      case 'CANCELLED': return 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100';
    }
  };

  // Theme-aware status style (inline) for scandinavian/other themes with status definitions
  const getStatusStyle = (status: string | undefined): React.CSSProperties | undefined => {
    if (!status) return undefined;
    const key = status.toLowerCase();
    const statusTheme = theme.components?.status;
    if (statusTheme && statusTheme[key]) {
      return {
        backgroundColor: statusTheme[key].bg,
        color: statusTheme[key].text,
        border: statusTheme[key].border ? `1px solid ${statusTheme[key].border}` : undefined,
      };
    }
    return undefined;
  };

  const getStatusLabel = (status: string | undefined): string => {
    if (!status) {
      return 'Unknown Status';
    }
    
    switch (status.toUpperCase()) {
      case 'PENDING': return 'Pending';
      case 'ACCEPTED': return 'Order Accepted';
      case 'PREPARING': return 'Preparing';
      case 'READY': return 'Ready for Pickup';
      case 'READY_FOR_PICKUP': return 'Ready for Pickup';
      case 'COMPLETED': return 'Completed';
      case 'CANCELLED': return 'Cancelled';
      default: return status;
    }
  };

  
  const renderOrderDetails = () => {
    if (loading) {
      return <div className="text-center p-8">Loading order details...</div>;
    }
    
    if (error) {
      return <div className="bg-red-100 dark:bg-red-900 p-4 rounded-md text-red-700 dark:text-red-200 text-center mb-4">{error}</div>;
    }
    
    if (!order) {
      return <div className="text-center p-8">Order not found</div>;
    }
    
    // Check for incomplete or corrupted order data
    if (!order.status || !order.id) {
      return (
        <div className="bg-red-100 dark:bg-red-900 p-4 rounded-md text-red-700 dark:text-red-200 text-center mb-4">
          Order data is unavailable or corrupt. 
          <button 
            onClick={handleBack} 
            className="block mx-auto mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      );
    }

    // Format the date for display
    const formattedDate = order.createdAt 
      ? `${formatDistanceToNow(new Date(order.createdAt))} ago`
      : '';

    return (
      <div className="shadow overflow-hidden" style={{
        backgroundColor: theme.components.card.background,
        borderRadius: '16px',
        border: `1px solid ${theme.components.card.border}`,
      }}>
        {/* Back button for navigation */}
        <div className="p-4 flex justify-between items-center" style={{ borderBottom: `1px solid ${theme.components.card.border}` }}>
          <button 
            onClick={handleBack}
            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            {isFromMyCart ? 'Back to Menu' : 'Back to Orders'}
          </button>
          
          {/* Status badge */}
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(order.status) ? '' : getStatusColor(order.status)}`}
            style={getStatusStyle(order.status)}
          >
            {getStatusLabel(order.status)}
          </div>
        </div>
        
        <div className="p-3 sm:p-4 lg:p-6">
          {/* Order header */}
          <div className="mb-3 sm:mb-4 lg:mb-6">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>
              Order {formatOrderId(order.customOrderId || `#${order.id}`, DisplayContext.CUSTOMER)}
            </h2>
            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
              <p>From {order.vendor?.username || 'Unknown Vendor'}</p>
              <p>{formattedDate}</p>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {order.deliveryType === 'DELIVER_TO_TABLE' ? 'Delivery to Table' : 'Pickup'}
            </p>
            {order.tableName && (
              <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <span className="text-lg">🍽️</span>
                <span className="text-sm">
                  <span className="font-medium text-blue-800 dark:text-blue-200">Table {order.tableName}</span>
                  {order.sectionName && <span className="text-blue-600 dark:text-blue-300"> • {order.sectionName}</span>}
                  {order.floorName && <span className="text-blue-600 dark:text-blue-300"> • {order.floorName}</span>}
                </span>
              </div>
            )}
          </div>
          
          {/* Order items */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Order Items</h3>
            {Array.isArray(order.items) && order.items.length > 0 ? (
              (() => {
                // Group order items by menu item ID + portion (for proper portion separation)
                // This ensures different portions are treated as separate line items
                const groupedItems = order.items.reduce((groups, item) => {
                  const menuItemId = item.menuItem?.id || item.id;
                  const portionKey = (item as any).portionName || (item as any).portionId || 'standard';
                  const key = `${menuItemId}-${portionKey}`;
                  
                  if (!groups[key]) {
                    groups[key] = {
                      menuItemId,
                      menuItemName: (item as any).menuItemName || item.menuItem?.name || 'Unknown Item',
                      imageUrl: (item as any).imageUrl || item.menuItem?.imageUrl || null,
                      portionName: (item as any).portionName || null,
                      portionId: (item as any).portionId || null,
                      items: []
                    };
                  }
                  groups[key].items.push(item);
                  return groups;
                }, {} as { [key: string]: {
                  menuItemId: number;
                  menuItemName: string;
                  imageUrl: string | null;
                  portionName: string | null;
                  portionId: number | null;
                  items: typeof order.items
                } });

                return (
                  <div className="space-y-4">
                    {Object.entries(groupedItems).map(([groupKey, group]) => {
                      const totalQuantity = group.items.reduce((sum, item) => sum + item.quantity, 0);
                      const totalPrice = group.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                      const unitPrice = group.items[0]?.price || 0;
                      const itemStyle = isModernTheme ? getItemEmoji(group.menuItemName) : null;

                      return (
                        <div 
                          key={groupKey} 
                          className="rounded-lg p-4 border"
                          style={isModernTheme && itemStyle ? {
                            backgroundColor: `${itemStyle.color}08`,
                            borderColor: `${itemStyle.color}25`,
                            boxShadow: `0 2px 8px ${itemStyle.color}10`
                          } : {
                            backgroundColor: theme.components.card.hover || 'rgb(249 250 251)',
                            borderColor: theme.components.card.border || 'rgb(229 231 235)'
                          }}
                        >
                          {/* Item Header - Each portion gets its own line */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {/* Item Image or Emoji */}
                                {group.imageUrl ? (
                                  <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden">
                                    <MenuItemImage imageUrl={group.imageUrl} itemName={group.menuItemName} size="thumbnail" />
                                  </div>
                                ) : isModernTheme && itemStyle ? (
                                  <span className="text-2xl">{itemStyle.emoji}</span>
                                ) : null}
                                <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                                  {group.menuItemName}
                                </h4>
                                {group.portionName && (
                                  <span 
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                                    style={isModernTheme && itemStyle ? {
                                      backgroundColor: `${itemStyle.color}20`,
                                      color: itemStyle.color
                                    } : {
                                      backgroundColor: theme.components?.badge?.info?.bg || 'rgb(219 234 254)',
                                      color: theme.components?.badge?.info?.text || 'rgb(30 64 175)'
                                    }}
                                  >
                                    {group.portionName}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <span>Qty: {totalQuantity}</span>
                                <span style={isModernTheme && itemStyle ? { color: itemStyle.color, fontWeight: 600 } : {}}>
                                  {CURRENCY.symbol}{unitPrice.toFixed(2)} each
                                </span>
                              </div>
                            </div>
                            <p 
                              className="font-bold text-lg"
                              style={isModernTheme && itemStyle ? { color: itemStyle.color } : { color: 'rgb(17 24 39)' }}
                            >
                              {CURRENCY.symbol}{totalPrice.toFixed(2)}
                            </p>
                          </div>

                          {/* Comments and Toppings */}
                          {group.items.some(item => item.comment || (item.toppings && item.toppings.length > 0) || (item as any).requestedSpiceLevel) && (
                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                              {group.items.map((item, index) => (
                                <div key={index}>
                                  {item.comment && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                      Note: {item.comment}
                                    </p>
                                  )}
                                  {(item as any).requestedSpiceLevel && (item as any).requestedSpiceLevel !== 'NONE' && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                      <span>Spice Level:</span>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        (item as any).requestedSpiceLevel === 'MILD' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                        (item as any).requestedSpiceLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                        (item as any).requestedSpiceLevel === 'HOT' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                        (item as any).requestedSpiceLevel === 'EXTRA_HOT' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                      }`}>
                                        {(item as any).requestedSpiceLevel === 'EXTRA_HOT' ? 'Extra Hot' : 
                                         (item as any).requestedSpiceLevel?.charAt(0) + (item as any).requestedSpiceLevel?.slice(1).toLowerCase()}
                                      </span>
                                    </div>
                                  )}
                                  {Array.isArray(item.toppings) && item.toppings.length > 0 && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      <span>Toppings: </span>
                                      {item.toppings.map((topping, i) => (
                                        <span key={topping?.id || i}>
                                          {topping?.name || 'Unknown Topping'}{i < item.toppings!.length - 1 ? ', ' : ''}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                No items found in this order.
              </div>
            )}
          </div>
          
          {/* Order total with tax breakdown */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="space-y-2">
              {/* Subtotal */}
              {order.subtotal !== undefined && order.subtotal !== null && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.subtotal)}</span>
                </div>
              )}

              {/* Discount — before taxes/service charge: they are computed on the
                  discounted base, so display order mirrors calculation order */}
              {order.discountAmount !== undefined && order.discountAmount !== null && order.discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Discount</span>
                  <span className="text-green-600 dark:text-green-400">-{formatCurrency(order.discountAmount)}</span>
                </div>
              )}

              {/* CGST */}
              {order.cgstAmount !== undefined && order.cgstAmount !== null && order.cgstAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    CGST {order.cgstRate ? `(${order.cgstRate}%)` : ''}
                  </span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.cgstAmount)}</span>
                </div>
              )}

              {/* SGST */}
              {order.sgstAmount !== undefined && order.sgstAmount !== null && order.sgstAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    SGST {order.sgstRate ? `(${order.sgstRate}%)` : ''}
                  </span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.sgstAmount)}</span>
                </div>
              )}

              {/* IGST */}
              {order.igstAmount !== undefined && order.igstAmount !== null && order.igstAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    IGST {order.igstRate ? `(${order.igstRate}%)` : ''}
                  </span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.igstAmount)}</span>
                </div>
              )}

              {/* Service Charge */}
              {order.serviceChargeAmount !== undefined && order.serviceChargeAmount !== null && order.serviceChargeAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Service Charge {order.serviceChargeRate ? `(${order.serviceChargeRate}%)` : ''}
                  </span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.serviceChargeAmount)}</span>
                </div>
              )}

              {/* Total */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 flex justify-between">
                <p className="font-medium text-gray-900 dark:text-white">Total</p>
                <p className="font-bold text-gray-900 dark:text-white">
                  {formatCurrency(
                    typeof order.total === 'number' && order.total > 0
                      ? order.total
                      : Array.isArray(order.items)
                        ? order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        : 0
                  )}
                </p>
              </div>
            </div>
          </div>
          
          {/* Action buttons - Only show for orders accessed from "My Cart" and only if PENDING or ACCEPTED */}
          {isFromMyCart && (order.status === 'PENDING' || order.status === 'ACCEPTED') && (
            <div className="mt-6 space-y-3">
              {/* Modify order button - only show if order is pending and from My Cart */}
              <button
                onClick={() => {
                  // Hydrate cart with current order data before navigating
                  hydrateCartFromOrder(order);
                  // Use setTimeout to ensure cart state is updated before navigation
                  setTimeout(() => {
                    // Navigate to checkout with order data
                    navigate('/checkout', { 
                      state: { 
                        vendorId: order.vendor?.id?.toString(),
                        qrCode: order.qrCode?.code,
                        orderId: order.id,
                        deliveryType: order.deliveryType,
                        isModification: true
                      }
                    });
                  }, 0);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Modify Order
              </button>
              
              {/* Cancel order button - only show if order can be cancelled and from My Cart */}
              <button
                onClick={handleCancelOrder}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Cancel Order
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <CardLayoutTheme
        title={isFromMyCart ? "My Current Order" : "Order Details"}
        categories={[{ id: 1, name: isFromMyCart ? "Current Order" : "Order Details" }]}
        renderItems={renderOrderDetails}
        hideSidebar={true}
      />
      {/* Toaster provided globally via ToastProvider */}
    </>
  );
};

export default OrderDetailWithErrorBoundary;